/**
 * seriesPanel.js — Отображение списка серий, авто-выбор инструмента/пресета, overlays
 * Extracted from uiManager.js (декомпозиция God Object)
 *
 * @module seriesPanel
 */

import { setActiveTool, getActiveTool } from '../tools/toolManager.js';
import { getRenderingEngine, getViewportIds } from '../core/viewport.js';
import { renderSeriesListWithThumbnails } from './studyToolsUI.js';
import { formatPatientName } from '../utils/helpers.js';
import { emit } from '../core/eventBus.js';
import { log } from '../utils/logger.js';
import { окноПоЗначениям } from '../core/wlApply.js';   // [12.0.49]
import { cornerstone } from '../core/init.js';
import { getCachedDataSet } from '../loaders/loaderManager.js';
import { показатьСведенияСерии } from './imageInfo.js';
import { отметитьПресетыПоМодальности } from './presetModality.js';   // [12.0.34]
import { отметитьПанельПоМодальности } from './modalityPanel.js';   // [12.0.55]

// ── Helpers (duplicated from uiManager for independence) ──
const $ = (id) => document.getElementById(id);
const $$ = (sel) => document.querySelectorAll(sel);

/** @type {Object} Shared context from uiManager */
let _ctx = null;

// ══════════════════════════════════════════════════════════════
// PUBLIC API
// ══════════════════════════════════════════════════════════════

/**
 * Initialize series panel module.
 * @param {Object} ctx - Shared context from uiManager
 * @param {function():string} ctx.getCurrentMode
 * @param {function():string} ctx.getCurrentLayout
 * @param {function():string} ctx.getCurrentViewportId
 * @param {function():Array} ctx.getAllSeries
 * @param {function(string):Promise} ctx.loadSeriesIntoViewport
 */
export function initSeriesPanel(ctx) {
  _ctx = ctx;
}

/**
 * Render series list in sidebar with thumbnails.
 * Delegates to studyToolsUI for actual rendering.
 * @param {Array} seriesList - Array of series objects from loader
 */
export function renderSeriesList(seriesList) {
  renderSeriesListWithThumbnails(seriesList, (seriesUID) => {
    _ctx.loadSeriesIntoViewport(seriesUID);
  });
  emit('seriesList:updated');
}

/**
 * Auto-select best tool for loaded data based on modality, series size, and current mode.
 * @param {string} seriesUID
 * @param {Array} [allSeriesCache] - Pre-fetched series list (avoids duplicate getAllSeries call)
 */
export function autoSelectTool(seriesUID, allSeriesCache) {
  const allSeries = allSeriesCache || _ctx.getAllSeries();
  const series = allSeries.find(s => s.seriesUID === seriesUID);
  if (!series) return;

  const mode = _ctx.getCurrentMode();
  const layout = _ctx.getCurrentLayout();
  let bestTool = 'WindowLevel';

  if (mode === '3d') {
    bestTool = 'TrackballRotate';
  } else if ((series.images || series.files || []).length > 30) {
    bestTool = 'StackScroll';
  } else if ((series.images || series.files || []).length === 1) {
    bestTool = 'WindowLevel';
  }

  // Активируем инструмент
  setActiveTool(bestTool);
  window._mobileActiveTool = bestTool;
  if (window.showToolIndicator) window.showToolIndicator(bestTool);
  $$('.tool-btn').forEach(b => b.classList.toggle('active', b.dataset.tool === bestTool));
  const label = $('active-tool-label');
  if (label) label.textContent = bestTool;

  const toolOverlay = $('overlay-tool');
  if (toolOverlay) toolOverlay.textContent = bestTool;

  if (window.showToolIndicator && window.innerWidth <= 768) {
    window.showToolIndicator(bestTool);
  }

  $$('#mobile-tools-drawer .m-tool-btn').forEach(b => {
    b.classList.toggle('active', b.dataset.tool === bestTool);
  });

  log.debug(`[UI] Auto-select tool: ${bestTool} (${(series.images || series.files || []).length} images, mode=${mode})`);
}

/**
 * Auto-select W/L and 3D preset by modality and series description.
 * @param {string} seriesUID
 * @param {Array} [allSeriesCache]
 */
/**
 * [MR] Авто-окно для не-КТ модальностей.
 * У МРТ/УЗИ шкала яркости произвольная (не Хаунсфилды), поэтому:
 *   1) берём Window Center/Width из DICOM-тегов (0028,1050/1051), если есть;
 *   2) иначе — реальный min/max пикселей уже загруженного кадра;
 *   3) иначе null — останется табличный пресет.
 */
/**
 * [10.3.2] Окно яркости по гистограмме уже загруженного кадра.
 *
 * Берём 1-й и 99-й процент значений: хвосты — это чёрные поля коллиматора и
 * пересвет по краю, они к изображению отношения не имеют, а окно портят.
 * Считаем по каждому 4-му пикселю: на кадре 1000×1000 это 250 тысяч отсчётов,
 * для гистограммы более чем достаточно, а времени втрое меньше.
 *
 * @returns {{ww:number, wc:number}|null} null, если кадр ещё не загружен
 */
function _windowFromPixels(imageId) {
  // [12.0.49] Расчёт — core/wlApply.js, один на открытие серии, чипы «по
  // кадру» и клавишу A. Числа массива кадра берутся КАК ЕСТЬ: загрузчик уже
  // применил к ним RescaleSlope/Intercept (preScale), а здесь их применяли
  // второй раз — у ПЭТ и МРТ Philips (наклон не 1) окно уезжало в разы.
  try {
    const img = cornerstone.cache?.getImage?.(imageId);
    return окноПоЗначениям(img?.getPixelData?.());
  } catch (e) {
    log.debug('[AutoWL] по кадру не получилось:', e.message);
    return null;
  }
}

/**
 * [10.3.2] Окно по КАДРУ, который сейчас на экране — для кнопки «Ангио (кино)».
 *
 * Отдельно от автоподбора при выборе серии: врач может нажать её в любой
 * момент, например пролистав до кадра с контрастом в сосуде, и получить окно
 * именно по этому кадру.
 */
export function autoWindowFromFrame() {
  try {
    const re = getRenderingEngine();
    const vpId = _ctx?.getCurrentViewportId?.() || getViewportIds()[0];
    const vp = re?.getViewport(vpId);
    const id = vp?.getCurrentImageId?.() || vp?.getImageIds?.()?.[vp?.getCurrentImageIdIndex?.() || 0];
    if (!id) return null;
    // [12.0.49] Сначала сам кадр на экране, потом файл без номера кадра.
    return _windowFromPixels(id) || _windowFromPixels(String(id).split('?frame=')[0]);
  } catch (e) { log.debug('[AutoWL] по текущему кадру:', e.message); return null; }
}

/** Модальности без объёма: объёмный пресет им назначать не за чем. */
const NO_VOLUME_MODALITY = ['XA', 'XRF', 'RF', 'US', 'CR', 'DX', 'MG', 'ES'];

function _autoWindowForSeries(series) {
  try {
    const imgs = series?.images || [];
    if (!imgs.length) return null;
    const id = imgs[Math.floor(imgs.length / 2)]?.imageId || imgs[0]?.imageId;
    if (!id) return null;

    // 1. Теги из распарсенного dataSet (есть сразу после анализа файлов)
    const ds = getCachedDataSet(id);
    if (ds) {
      const wc = ds.floatString('x00281050', 0);
      const ww = ds.floatString('x00281051', 0);
      if (Number.isFinite(wc) && Number.isFinite(ww) && ww > 0) return { ww, wc };
    }

    // 1.5 Naturalized-метаданные загрузчика (не вытесняются нашим LRU)
    try {
      const nat = cornerstone.metaData.get('instance', id);
      let wcN = Array.isArray(nat?.WindowCenter) ? nat.WindowCenter[0] : nat?.WindowCenter;
      let wwN = Array.isArray(nat?.WindowWidth) ? nat.WindowWidth[0] : nat?.WindowWidth;
      wcN = parseFloat(wcN); wwN = parseFloat(wwN);
      if (Number.isFinite(wcN) && Number.isFinite(wwN) && wwN > 0) return { ww: wwN, wc: wcN };
    } catch (e) { log.debug('[AutoWL] nat:', e.message); }

    // 2. Метаданные движка (появляются после загрузки кадра)
    const voi = cornerstone.metaData.get('voiLutModule', id);
    let wc2 = Array.isArray(voi?.windowCenter) ? voi.windowCenter[0] : voi?.windowCenter;
    let ww2 = Array.isArray(voi?.windowWidth) ? voi.windowWidth[0] : voi?.windowWidth;
    wc2 = parseFloat(wc2); ww2 = parseFloat(ww2);
    if (Number.isFinite(wc2) && Number.isFinite(ww2) && ww2 > 0) return { ww: ww2, wc: wc2 };

    // 3. Окно по самому кадру.
    //
    // [10.3.2] Считаем по 1-му и 99-му проценту яркости, а не по min/max.
    //
    // Зачем. На ангиографии с С-дуги в кадре всегда есть чёрные поля от
    // коллиматора и пересвеченный край вне тела. Крайние значения задают min и
    // max, окно растягивается на весь диапазон — и сосуды выходят серой кашей
    // на сером фоне. Проценты отбрасывают эти хвосты, и контраст становится
    // таким, какой на снимке и должен быть.
    const byPix = _windowFromPixels(id);
    if (byPix) return byPix;
  } catch (e) {
    log.debug('[AutoWL] fallback to table preset:', e.message);
  }
  return null;
}

export function autoPresetByModality(seriesUID, allSeriesCache) {
  const series = (allSeriesCache || _ctx.getAllSeries()).find(s => s.seriesUID === seriesUID);
  if (!series) return;

  const mod = (series.modality || '').toUpperCase().split('/')[0].trim();
  const desc = (series.seriesDesc || series.studyDesc || '').toLowerCase();

  // ── W/L пресеты по модальности ──
  const WL_PRESETS = {
    'CT':  { ww: 400, wc: 40 },
    'MR':  { ww: 800, wc: 400 },
    'MG':  { ww: 2048, wc: 1024 },
    'US':  { ww: 255, wc: 128 },
    'XR':  { ww: 2048, wc: 1024 },
    'CR':  { ww: 2048, wc: 1024 },
    'DX':  { ww: 2048, wc: 1024 },
    'PT':  { ww: 30000, wc: 15000 },
    'NM':  { ww: 1000, wc: 500 },
  };

  const CT_DESC_PRESETS = {
    'lung':   { ww: 1500, wc: -500 },
    'лёгк':  { ww: 1500, wc: -500 },
    'chest':  { ww: 1500, wc: -500 },
    'грудн':  { ww: 1500, wc: -500 },
    'bone':   { ww: 1500, wc: 400 },
    'кост':   { ww: 1500, wc: 400 },
    'brain':  { ww: 80, wc: 40 },
    'мозг':   { ww: 80, wc: 40 },
    'head':   { ww: 80, wc: 40 },
    'голов':  { ww: 80, wc: 40 },
    'abdom':  { ww: 400, wc: 40 },
    'живот':  { ww: 400, wc: 40 },
    'liver':  { ww: 150, wc: 70 },
    'печен':  { ww: 150, wc: 70 },
    'angio':  { ww: 600, wc: 200 },
    'ангио':  { ww: 600, wc: 200 },
    'cardio': { ww: 500, wc: 100 },
    'сердц':  { ww: 500, wc: 100 },
    'coronar':{ ww: 500, wc: 200 },
    'коронар':{ ww: 500, wc: 200 },
  };

  const RENDER_PRESETS = {
    'CT':  'ct-bone-clean',
    'MR':  'mri-brain-3d',
    'PT':  'pet-hot',
  };

  const CT_3D_PRESETS = {
    'lung':   'ct-lung-3d',
    'лёгк':  'ct-lung-3d',
    'angio':  'ct-vascular',
    'ангио':  'ct-vascular',
    'cardiac':'cardiac-3d',
    'сердц':  'cardiac-3d',
    'brain':  'mri-brain-3d',
    'мозг':   'mri-brain-3d',
    'bone':   'ct-bone-clean',
    'кост':   'ct-bone-clean',
  };

  // Выбираем W/L
  let preset = WL_PRESETS[mod] || { ww: 400, wc: 40 };
  if (mod === 'CT') {
    for (const [key, val] of Object.entries(CT_DESC_PRESETS)) {
      if (desc.includes(key)) { preset = val; break; }
    }
  } else if (mod) {
    // [MR] Не-КТ: реальное окно из DICOM/пикселей вместо жёсткой таблицы.
    // Не нашлось — НИЧЕГО не применяем: родное окно кадра честнее заглушки 800/400
    // (именно она давала «вспышку» между родным и финальным окном).
    const autoWL = _autoWindowForSeries(series);
    preset = autoWL || null;

    // [10.3.2] Кадр мог ещё не догрузиться — тогда считать окно не по чему.
    //
    // Ровно так и выходило на ангиографии: подбор окна выполняется сразу
    // после выбора серии, а пиксели приезжают позже. Окно оставалось
    // нетронутым — весь диапазон 12 бит, W 4096 / L 2048, — и снимок выглядел
    // блёклым. Пробуем ещё несколько раз, пока кадр не появится.
    if (!preset && NO_VOLUME_MODALITY.includes(mod)) {
      let tries = 0;
      const again = () => {
        if (++tries > 6) return;
        const w = _autoWindowForSeries(series);
        if (!w) { setTimeout(again, 400); return; }
        try {
          const re2 = getRenderingEngine();
          const vp2 = re2?.getViewport(_ctx.getCurrentViewportId() || getViewportIds()[0]);
          if (vp2?.setProperties) {
            vp2.setProperties({ voiRange: { lower: w.wc - w.ww / 2, upper: w.wc + w.ww / 2 } });
            vp2.render();
            updateWLOverlay(w.ww, w.wc);
            log.info(`[AutoWL] окно по кадру: W:${w.ww} L:${w.wc} (${mod})`);
          }
        } catch (e) { log.debug('[AutoWL] повтор:', e.message); }
      };
      setTimeout(again, 400);
    }
  }

  // Применяем W/L
  try {
    const re = getRenderingEngine();
    if (re) {
      const vpId = _ctx.getCurrentViewportId() || getViewportIds()[0];
      const vp = re.getViewport(vpId);
      if (vp && vp.setProperties && preset) {
        const ww = preset.ww, wc = preset.wc;
        vp.setProperties({ voiRange: { lower: wc - ww / 2, upper: wc + ww / 2 } });
        vp.render();
        updateWLOverlay(ww, wc);
      }
    }
  } catch (e) {
    log.warn('[AutoPreset] W/L failed:', e);
  }

  // Подсвечиваем соответствующий preset chip.
  //
  // [10.3.0] preset бывает null — и тогда здесь падало «Cannot read properties
  // of null (reading 'ww')». Ветка выше (применение окна) на null проверена, а
  // эта — нет. Вылезало при открытии киносъёмки с ангиографа: подбирать окно
  // там не по чему, в тегах нет ни WindowCenter, ни WindowWidth. Снимок уже
  // загружен и показан, а человек видит красную ошибку на ровном месте.
  $$('.preset-chip[data-ww]').forEach(c => { c.classList.remove('active'); c.setAttribute('aria-pressed', 'false'); });
  if (!preset) {
    log.debug('[AutoPreset] окно по тегам не подобрано — подсвечивать нечего');
    return;
  }
  // [11.1.17] Подсвечиваются ВСЕ чипы с совпавшим окном, а не первый
  // попавшийся: при трёх чипах 400/40 (Живот/Грудная/Таз) прежний find
  // зажигал «Живот» на грудной и тазовой сериях — механика верна, подпись
  // лгала (аудит §2.4). Совпадение по числам, поэтому честно горят все
  // клинические синонимы разом — на обеих поверхностях.
  [...$$('.preset-chip[data-ww]')]
    .filter(c => parseInt(c.dataset.ww) === preset.ww && parseInt(c.dataset.wc) === preset.wc)
    .forEach(c => { c.classList.add('active'); c.setAttribute('aria-pressed', 'true'); });

  // 3D render preset
  const mode = _ctx.getCurrentMode();
  const layout = _ctx.getCurrentLayout();
  if ((mode === '3d' || layout === 'mpr') && !NO_VOLUME_MODALITY.includes(mod)) {
    // [10.3.2] Плоским сериям объёмный пресет не назначаем.
    //
    // Здесь стояло `RENDER_PRESETS[mod] || 'ct-bone-clean'`, и для ангиографии
    // (XA) программа выбирала «Кости жёсткие» — пресет объёмного рендеринга,
    // к плоской съёмке отношения не имеющий. Он подсвечивался в панели и
    // применялся нажатием, хотя объёма у такой серии нет вовсе.
    let rPreset = RENDER_PRESETS[mod] || 'ct-bone-clean';
    if (mod === 'CT') {
      for (const [key, val] of Object.entries(CT_3D_PRESETS)) {
        if (desc.includes(key)) { rPreset = val; break; }
      }
    }
    // [10.3.6] Сначала ищем кнопку ЛЕВОГО сайдбара, потом любую другую.
    // Раньше здесь стоял только `.preset-chip` — это была кнопка правого
    // сайдбара. Правый список пресетов убран как дубль левого, и без этой
    // правки автопресет по модальности жал бы кнопку в скрытом мобильном
    // ящике: работать бы работало, но концов потом не найти.
    const rChip = document.querySelector(
      `.sl-vrt-btn[data-render-preset="${rPreset}"], .preset-chip[data-render-preset="${rPreset}"]`
    );
    if (rChip) rChip.click();
  }

  log.debug(`[UI] Auto-preset: ${mod} → W:${preset.ww}/L:${preset.wc} (desc: "${desc.substring(0, 30)}")`);
}

/**
 * Update viewport overlays (patient info, series info, slice count, etc.)
 * @param {string} seriesUID
 */
/** [RICH] Плоскость среза с углом наклона из Image Orientation (как «OAx 45,9» у GE) */
function _planeFromIOP(iop) {
  if (!Array.isArray(iop) || iop.length !== 6 || !iop.every(Number.isFinite)) return '';
  const [r1, r2, r3, c1, c2, c3] = iop;
  const n = [r2 * c3 - r3 * c2, r3 * c1 - r1 * c3, r1 * c2 - r2 * c1];
  const len = Math.hypot(n[0], n[1], n[2]) || 1;
  const ax = [Math.abs(n[0]) / len, Math.abs(n[1]) / len, Math.abs(n[2]) / len];
  const i = ax.indexOf(Math.max(ax[0], ax[1], ax[2]));
  const ang = Math.acos(Math.min(1, ax[i])) * 180 / Math.PI;
  return (ang > 5 ? 'O' : '') + ['Sag', 'Cor', 'Ax'][i] + (ang > 1 ? ` ${ang.toFixed(1)}°` : '');
}

/**
 * [10.10.0] Заполнить блок сведений — В ЛЮБОМ корне, а не только в единственном.
 *
 * Блок сведений о пациенте и съёмке был один на всю программу: два десятка
 * элементов по идентификаторам, и писалось в них по «текущей» серии. Пока окно
 * было одно, разницы не было. В сравнении двух исследований это вылезло сразу:
 * слева полные сведения, справа одна дата — врач видит, что лежит в левом
 * окне, и не видит, что в правом.
 *
 * Поэтому поля ищутся по пометке `data-ov`, а корень передаётся снаружи. Вызов
 * без корня работает ровно как прежде, по всему документу; сравнение передаёт
 * свой блок в каждом окне и получает те же сведения по своей серии. Логика
 * заполнения одна на оба случая: заводить вторую копию этих полутора сотен
 * строк было бы худшим из решений — мы в этой программе целыми версиями
 * вычищали двойников.
 *
 * @param {string} seriesUID
 * @param {Element|Document} [корень] — где искать поля блока сведений
 */
export function updateOverlays(seriesUID, корень) {
  // Поля блока — в переданном корне; всё остальное (строки левой панели) —
  // как прежде, по документу: они одни на программу и к окну не относятся.
  const $ = (id) => (String(id).startsWith('overlay-')
    ? (корень || document).querySelector(`[data-ov="${String(id).slice(8)}"]`)
    : document.getElementById(id));
  const series = _ctx.getAllSeries().find(s => s.seriesUID === seriesUID);
  if (!series) return;

  // Верх-лево: пациент, исследование, институция
  const op = $('overlay-patient');
  if (op) op.textContent = formatPatientName(series.patientName);
  const os = $('overlay-study');
  if (os) os.textContent = series.studyDesc || series.seriesDesc || '';
  const inst = $('overlay-institution');
  if (inst) inst.textContent = series.institution || '';

  // Верх-право: текущий инструмент
  const toolOverlay = $('overlay-tool');
  if (toolOverlay) {
    const activeTool = getActiveTool ? getActiveTool() : '';
    toolOverlay.textContent = activeTool || '';
  }

  // Низ-лево: срез, толщина, пиксельный размер
  const _seriesImages = series.images || series.files || [];
  const os2 = $('overlay-slice');
  if (os2) os2.textContent = `Срез: 1 / ${_seriesImages.length}`;
  const oth = $('overlay-thickness');
  if (oth) oth.textContent = series.sliceThickness ? `Толщина: ${series.sliceThickness} мм` : 'Толщина: —';
  const ops = $('overlay-pixel-spacing');
  if (ops) ops.textContent = series.pixelSpacing ? `Пиксель: ${series.pixelSpacing}` : '';

  // Низ-право: модальность, размер, дата
  const om = $('overlay-modality');
  if (om) om.textContent = series.modality;
  const oi = $('overlay-size');
  if (oi) oi.textContent = `${_seriesImages.length} images`;
  const od = $('overlay-date');
  if (od) {
    const d = series.studyDate;
    od.textContent = (d && d.length >= 8)
      ? `${d.substring(6,8)}.${d.substring(4,6)}.${d.substring(0,4)}`
      : '';
  }

  // [12.0.25] Раздел «Информация» левой панели — все шесть строк, по-русски.
  // Здесь писались только две («Patient: …», «Series: …», по-английски),
  // остальные четыре не заполнял никто. Строки собирает imageInfoCore.js.
  //
  // Только при основном показе, без корня. Сравнение зовёт updateOverlays
  // для каждого своего окна, и раздел, один на программу, получал сведения
  // ТОГО исследования, чьё окно отрисовалось последним: слева в списке —
  // одна серия, в «Информации» — другая. Сравнению раздел не нужен: у его
  // окон свои блоки сведений.
  if (!корень) показатьСведенияСерии(series);
  // [12.0.34] Пресеты чужой модальности — серыми и неактивными: при МРТ
  // гаснут пресеты КТ, при КТ — пресеты МРТ. Тоже только для основного показа.
  if (!корень) отметитьПресетыПоМодальности(series.modality);
  // [12.0.55] Правая панель — только то, что работает на этой модальности:
  // на МРТ нет эмфиземы и порогов в HU, на УЗИ — масок и 3D. Владелец: «в
  // каждом режиме только те кнопки, которые ему предназначены».
  if (!корень) отметитьПанельПоМодальности(series.modality);

  // [RICH] Подробный оверлей — как у станционного вьювера (виден при body.overlay-rich)
  // Источники тегов: (1) наш кэш dataSet; (2) naturalized-метаданные загрузчика.
  try {
    const firstId = _seriesImages[0]?.imageId;
    const ds = firstId ? getCachedDataSet(firstId) : null;
    let nat = null;
    try { nat = firstId ? cornerstone.metaData.get('instance', firstId) : null; } catch (e) { nat = null; }
    const t = (tagHex, kw) => {
      try { const v = ds?.string(tagHex); if (v && v.trim()) return v.trim(); } catch (e) { /* нет в кэше */ }
      let nv = nat ? nat[kw] : undefined;
      if (nv == null) return '';
      if (Array.isArray(nv)) nv = nv[0];
      return String(nv).trim();
    };

    // Верх-лево: аппарат, Ex/Se/Im, плоскость (+C при контрасте)
    const dev = $('overlay-device');
    if (dev) dev.textContent = [t('x00080070', 'Manufacturer'), t('x00081090', 'ManufacturerModelName'),
      t('x00180087', 'MagneticFieldStrength') ? t('x00180087', 'MagneticFieldStrength') + 'T' : ''].filter(Boolean).join(' ');
    const ex = $('overlay-exseim');
    if (ex) {
      ex.dataset.ex = t('x00200010', 'StudyID'); ex.dataset.se = t('x00200011', 'SeriesNumber');
      ex.textContent = `Ex:${ex.dataset.ex || '—'}  Se:${ex.dataset.se || '—'}  Im:1/${_seriesImages.length}`;
    }
    const contrast = t('x00180010', 'ContrastBolusAgent');
    const plane = $('overlay-plane');
    if (plane) plane.textContent = _planeFromIOP(_seriesImages.find(im => Array.isArray(im.iop))?.iop) + (contrast ? ' +C' : '');

    // Верх-право: пол/возраст/ID, ДР и время исследования
    const demo = $('overlay-demo');
    if (demo) demo.textContent = [t('x00100040', 'PatientSex'), t('x00101010', 'PatientAge'),
      t('x00100020', 'PatientID') ? 'ID:' + t('x00100020', 'PatientID') : ''].filter(Boolean).join('  ');
    const dbt = $('overlay-dobtime');
    if (dbt) {
      const dob = t('x00100030', 'PatientBirthDate');
      const st = t('x00080030', 'StudyTime');
      const stf = st && st.length >= 6 ? `${st.substring(0,2)}:${st.substring(2,4)}:${st.substring(4,6)}` : '';
      dbt.textContent = [dob && dob.length >= 8 ? `ДР: ${dob.substring(6,8)}.${dob.substring(4,6)}.${dob.substring(0,4)}` : '', stf]
        .filter(Boolean).join('  ·  ');
    }

    // Низ-лево: протокол/последовательность + опции скана
    const proto = $('overlay-protocol');
    if (proto) {
      // Имя последовательности: приватный тег GE (0019,109C) надёжнее стандартных —
      // проверено на реальном файле (PROP для PROPELLER); (0019,109E) — семейство (FSE)
      let psd = '', fam = '';
      try { psd = (ds?.string('x0019109c') || '').trim(); } catch (e) { psd = ''; }
      try { fam = (ds?.string('x0019109e') || '').trim(); } catch (e) { fam = ''; }
      const seqName = (psd ? psd.toUpperCase() + (fam && fam.toUpperCase() !== psd.toUpperCase() ? ` (${fam})` : '') : '')
        || t('x00180024', 'SequenceName') || t('x00181030', 'ProtocolName');
      let so = nat?.ScanOptions;
      if (Array.isArray(so)) so = so.join('\\');
      if (!so) { try { so = ds?.string('x00180022') || ''; } catch (e) { so = ''; } }
      const soClean = String(so).split('\\').filter(x => x && !/_GEMS$/i.test(x)).join('/');
      proto.textContent = [seqName, soClean].filter(Boolean).join(' · ');
    }

    // Параметры последовательности: TR TE TI EC FA BW NEX ET (МР) / kVp mAs (КТ)
    const seq = $('overlay-seqparams');
    if (seq) {
      if (series.modality === 'MR') {
        const tr = parseFloat(t('x00180080', 'RepetitionTime')), te = parseFloat(t('x00180081', 'EchoTime'));
        const ti = parseFloat(t('x00180082', 'InversionTime')), ec = t('x00180086', 'EchoNumbers');
        const fa = parseFloat(t('x00181314', 'FlipAngle')), bw = parseFloat(t('x00180095', 'PixelBandwidth'));
        const nex = parseFloat(t('x00180083', 'NumberOfAverages')), et = t('x00180091', 'EchoTrainLength');
        seq.textContent = [
          Number.isFinite(tr) ? `TR:${Math.round(tr)}` : '', Number.isFinite(te) ? `TE:${Math.round(te)}` : '',
          Number.isFinite(ti) ? `TI:${Math.round(ti)}` : '', ec ? `EC:${ec}` : '',
          Number.isFinite(fa) ? `FA:${Math.round(fa)}°` : '', Number.isFinite(bw) ? `BW:${Math.round(bw)}` : '',
          Number.isFinite(nex) ? `NEX:${(+nex.toFixed(2))}` : '', et ? `ET:${et}` : ''
        ].filter(Boolean).join('  ');
      } else {
        const kvp = t('x00180060', 'KVP'), mas = t('x00181152', 'Exposure');
        seq.textContent = [kvp ? `kVp:${kvp}` : '', mas ? `mAs:${mas}` : ''].filter(Boolean).join('  ');
      }
    }
    const coil = $('overlay-coil');
    if (coil) { const c = t('x00181250', 'ReceiveCoilName'); coil.textContent = c ? `Катушка: ${c}` : ''; }

    // Геометрия: съёмочная матрица, реконструкция, FOV двумя сторонами, толщина/интервал
    const geom = $('overlay-geom');
    if (geom) {
      let acqM = '';
      try {
        let a = nat?.AcquisitionMatrix;
        if (!Array.isArray(a) && ds) a = [0, 1, 2, 3].map(i => ds.intString('x00181310', i));
        if (Array.isArray(a) && a.length === 4) {
          const f = a[0] || a[1], ph = a[2] || a[3];
          if (f && ph) acqM = `${f}×${ph}`;
        }
      } catch (e) { acqM = ''; }
      const rows = parseFloat(t('x00280010', 'Rows')), cols = parseFloat(t('x00280011', 'Columns'));
      let psr, psc;
      try {
        let psv = nat?.PixelSpacing;
        if (!psv && ds) { const s0 = ds.string('x00280030'); if (s0) psv = s0.split('\\'); }
        if (Array.isArray(psv)) { psr = parseFloat(psv[0]); psc = parseFloat(psv[1] ?? psv[0]); }
      } catch (e) { psr = psc = undefined; }
      let fov = '';
      if (Number.isFinite(rows) && Number.isFinite(cols) && Number.isFinite(psr) && Number.isFinite(psc)) {
        fov = `${(cols * psc / 10).toFixed(1)}×${(rows * psr / 10).toFixed(1)} см`;
      } else {
        const rd = parseFloat(t('x00181100', 'ReconstructionDiameter'));
        if (Number.isFinite(rd)) fov = `${(rd / 10).toFixed(1)}×${(rd / 10).toFixed(1)} см`;
      }
      const thk = parseFloat(t('x00180050', 'SliceThickness'));
      const sbs = parseFloat(t('x00180088', 'SpacingBetweenSlices'));
      let thkStr = '';
      if (Number.isFinite(thk)) {
        thkStr = `${thk.toFixed(2)}`;
        if (Number.isFinite(sbs)) { const gap = sbs - thk; thkStr += `/${gap >= 0 ? '+' : ''}${gap.toFixed(2)}ов`; }
        thkStr += ' мм';
      }
      // [6.0.0] Основная строка «Толщина» берётся из тегов, если серия её не дала:
      // GE-стиль «толщина · интервал» — сверено с рамкой конкурента (1,20thk/-0,60ov)
      if (Number.isFinite(thk)) {
        const othEl = $('overlay-thickness');
        if (othEl) othEl.textContent = `Толщина: ${thk.toFixed(2)} мм` + (Number.isFinite(sbs) ? ` · интервал ${sbs.toFixed(2)}` : '');
      }
      // [GE] Длительность скана — приватный тег (0019,105A), микросекунды.
      // Сверено с рамкой GE: 119 664 000 µs = 02:00 у серии «45/02:00»
      let scanStr = '';
      try {
        const dur = ds?.float?.('x0019105a');
        if (Number.isFinite(dur) && dur > 0) {
          const sec = dur > 100000 ? dur / 1e6 : dur;
          if (sec > 1 && sec < 36000) {
            const mm = Math.floor(sec / 60), ss = Math.round(sec % 60);
            scanStr = `Скан: ${_seriesImages.length}/${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
          }
        }
      } catch (e) { scanStr = ''; }
      geom.textContent = [scanStr, acqM ? `Съёмка: ${acqM}` : '',
        Number.isFinite(rows) && Number.isFinite(cols) ? `Рекон: ${cols}×${rows}` : '',
        fov ? `FOV: ${fov}` : '', thkStr].filter(Boolean).join('  ·  ');
    }
  } catch (e) { log.debug('[Overlay/rich]', e.message); }

  emit('overlays:updated', seriesUID);
}

/**
 * Update W/L overlay text.
 * @param {number} ww - Window width
 * @param {number} wc - Window center
 */
export function updateWLOverlay(ww, wc) {
  const el = $('overlay-wl');
  if (el) el.textContent = `W: ${ww} / L: ${wc}`;
}
// ══════════════════════════════════════════════════════════════
// ══════════════════════════════════════════════════════════════
// ENTERPRISE FEATURES (v6) — Sort, Favorites, Multi-select, Navigate
// ══════════════════════════════════════════════════════════════

const FAV_SERIES_KEY = 'mikhmel_fav_series';
let _sortMode = 'default'; // 'default'|'count-desc'|'count-asc'|'modality'|'description'

/**
 * Sort series list by criteria and re-render
 * @param {'default'|'count-desc'|'count-asc'|'modality'|'description'} mode
 */
export function sortSeries(mode) {
  _sortMode = mode;
  const all = [...(_ctx?.getAllSeries?.() || [])];
  const sorters = {
    'count-desc': (a, b) => (b.images || b.files || []).length - (a.images || a.files || []).length,
    'count-asc': (a, b) => (a.images || a.files || []).length - (b.images || b.files || []).length,
    'modality': (a, b) => (a.modality || '').localeCompare(b.modality || ''),
    'description': (a, b) => (a.seriesDesc || '').localeCompare(b.seriesDesc || ''),
  };
  if (sorters[mode]) all.sort(sorters[mode]);
  renderSeriesListWithThumbnails(all, (uid) => _ctx?.loadSeriesIntoViewport?.(uid));
  emit('seriesList:sorted', { mode, count: all.length });
  return all.length;
}

/**
 * Toggle series as favorite (persisted)
 * @param {string} seriesUID
 * @returns {boolean} — true if now favorite
 */
export function toggleSeriesFavorite(seriesUID) {
  const favs = _getFavorites();
  if (favs.has(seriesUID)) favs.delete(seriesUID); else favs.add(seriesUID);
  try { localStorage.setItem(FAV_SERIES_KEY, JSON.stringify([...favs])); } catch { /* non-critical */ }
  // Visual update
  const item = document.querySelector(`.series-item[data-series-uid="${seriesUID}"]`);
  if (item) item.classList.toggle('series-favorite', favs.has(seriesUID));
  return favs.has(seriesUID);
}

/**
 * Get favorite series UIDs
 * @returns {string[]}
 */
export function getFavoriteSeries() { return [..._getFavorites()]; }

/**
 * Show only favorite series
 */
export function showOnlyFavorites() {
  const favs = _getFavorites();
  const all = _ctx?.getAllSeries?.() || [];
  const filtered = all.filter(s => favs.has(s.seriesUID));
  renderSeriesListWithThumbnails(filtered, (uid) => _ctx?.loadSeriesIntoViewport?.(uid));
  return filtered.length;
}

function _getFavorites() {
  try { return new Set(JSON.parse(localStorage.getItem(FAV_SERIES_KEY) || '[]')); } catch { return new Set(); }
}

/** Navigate to next/prev series */
export function navigateSeriesDirection(direction) {
  const all = _ctx?.getAllSeries?.() || [];
  if (!all.length) return false;
  const cur = _ctx?.getCurrentSeries?.();
  const idx = all.findIndex(s => s.seriesUID === cur);
  let next = direction === 'next' ? idx + 1 : idx - 1;
  if (next < 0) next = all.length - 1;
  if (next >= all.length) next = 0;
  _ctx?.loadSeriesIntoViewport?.(all[next].seriesUID);
  return true;
}

export function navigateToSeries(index) {
  const all = _ctx?.getAllSeries?.() || [];
  if (index >= 0 && index < all.length) { _ctx?.loadSeriesIntoViewport?.(all[index].seriesUID); return true; }
  return false;
}

export function filterSeriesByModality(modality) {
  const all = _ctx?.getAllSeries?.() || [];
  const filtered = modality ? all.filter(s => (s.modality || '').toUpperCase().startsWith(modality.toUpperCase())) : all;
  renderSeriesListWithThumbnails(filtered, (uid) => _ctx?.loadSeriesIntoViewport?.(uid));
  return filtered.length;
}

export function searchSeries(query) {
  const all = _ctx?.getAllSeries?.() || [];
  const q = (query || '').toLowerCase();
  const filtered = q ? all.filter(s => (s.seriesDesc || '').toLowerCase().includes(q) || (s.modality || '').toLowerCase().includes(q)) : all;
  renderSeriesListWithThumbnails(filtered, (uid) => _ctx?.loadSeriesIntoViewport?.(uid));
  return filtered.length;
}

export function getSeriesListState() {
  const all = _ctx?.getAllSeries?.() || [];
  return { totalSeries: all.length, sortMode: _sortMode, favorites: _getFavorites().size,
    modalities: [...new Set(all.map(s => s.modality).filter(Boolean))] };
}

export function getAutoPresetInfo(seriesUID) {
  const series = (_ctx?.getAllSeries?.() || []).find(s => s.seriesUID === seriesUID);
  if (!series) return { error: 'not found' };
  return { modality: series.modality, description: series.seriesDesc, images: (series.images || series.files || []).length };
}
